/**
 * Schedule model for the new «الجدول» page (SCHEDULE-BUILD-PLAN.md §A.2, §E). Pure functions, no React, no i18n.
 *
 *   1. adapter   - UI weekday index (Sunday = 0) <-> backend mask (Monday = bit 0 ... Sunday = bit 6)
 *   2. mapping   - a UI rule/event <-> the Rules API payload / overview entry (NULL times = all day)
 *   3. engine    - the occurrence-based evaluator (the same rule PHP `pickV2` and the player use), checked against the
 *                  shared vectors file (`__fixtures__/schedule_v2_vectors.json`, sha256-pinned in the tests)
 *   4. preview   - now / next / week built on the engine (no clock stepping for now/next: real window boundaries)
 *
 * Times are venue wall-clock minutes (0..1439) in the UI and 'HH:MM:SS' on the wire. Dates are 'YYYY-MM-DD' strings and
 * are only ever handled with UTC calendar arithmetic, so the browser's own time zone never leaks into a date.
 */

/* ───────────────────────── 1. weekday adapter ───────────────────────── */

export const EVERY_DAY = [0, 1, 2, 3, 4, 5, 6];
export const WEEKDAYS_SUN_THU = [0, 1, 2, 3, 4];
export const WEEKEND_FRI_SAT = [5, 6];

/** UI index (0 = Sunday ... 6 = Saturday) -> backend bit (0 = Monday ... 6 = Sunday). */
export const uiDayToBit = (i) => (i + 6) % 7;
/** backend bit -> UI index. */
export const bitToUiDay = (b) => (b + 1) % 7;

/** UI day indexes -> backend `days_of_week` mask. */
export function daysToMask(days) {
  return [...new Set(days)].reduce((m, i) => m | (1 << uiDayToBit(i)), 0);
}

/** Backend mask -> sorted UI day indexes. NULL (every day) -> all seven. */
export function maskToDays(mask) {
  if (mask === null || mask === undefined) return [...EVERY_DAY];
  const out = [];
  for (let b = 0; b < 7; b += 1) if (mask & (1 << b)) out.push(bitToUiDay(b));
  return out.sort((a, b) => a - b);
}

/* ───────────────────────── dates and times (strings, UTC arithmetic) ───────────────────────── */

const pad2 = (n) => String(n).padStart(2, '0');

const utcOf = (ymd) => {
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map((x) => parseInt(x, 10));
  return Date.UTC(y, m - 1, d);
};
const ymdOfUtc = (ms) => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

export const addDays = (ymd, n) => ymdOfUtc(utcOf(ymd) + n * 86400000);
export const dayDiff = (a, b) => Math.round((utcOf(b) - utcOf(a)) / 86400000);
/** 0 = Sunday ... 6 = Saturday (UI index). */
export const dowOf = (ymd) => new Date(utcOf(ymd)).getUTCDay();
export const ymdParts = (ymd) => {
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map((x) => parseInt(x, 10));
  return { y, m, d };
};
/** Same month/day, `n` years later. 29 Feb moves to 28 Feb when the target year is not a leap year. */
export function addYears(ymd, n) {
  const { y, m, d } = ymdParts(ymd);
  const ty = y + n;
  const leap = (ty % 4 === 0 && ty % 100 !== 0) || ty % 400 === 0;
  const td = m === 2 && d === 29 && !leap ? 28 : d;
  return `${ty}-${pad2(m)}-${pad2(td)}`;
}

/** 'HH:MM[:SS]' -> minutes since midnight (null stays null). */
export function hmsToMin(t) {
  if (t === null || t === undefined || t === '') return null;
  const [h, m] = String(t).split(':');
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}
/** minutes -> 'HH:MM:SS' (wraps at 24 h, so 1440 is '00:00:00'). */
export function minToHms(min) {
  const m = ((min % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}:00`;
}

/* ───────────────────────── 2. UI rule/event <-> API ───────────────────────── */

/**
 * The Rules API body for a form (SCHEDULE-BUILD-PLAN.md §E). `where` ids are group ids or screen (device) ids; `all` sends none.
 *   weekly: days -> mask (Sunday-first adapter), from === to = all day = both times NULL
 *   event:  dates, name, all day = both times NULL, else the daily hours
 */
export function formToPayload(form, { resolve } = {}) {
  const where = form.where.mode === 'all' ? { mode: 'all' } : { mode: form.where.mode, ids: [...form.where.ids] };
  let body;
  if (form.kind === 'once') {
    // a single-day event with hours (base plan). "Until the end of the day" is 24:00, stored as 00:00:00 = the next midnight.
    body = {
      campaign_id: form.show,
      kind: 'event',
      name: String(form.oname || '').trim(),
      start_date: form.odate,
      end_date: form.odate,
      start_time: minToHms(form.ofrom),
      end_time: minToHms(form.oto),
      where,
    };
  } else if (form.kind === 'rule') {
    const allDay = form.from === form.to;
    body = {
      campaign_id: form.show,
      kind: 'weekly',
      days_of_week: daysToMask(form.days),
      start_time: allDay ? null : minToHms(form.from),
      end_time: allDay ? null : minToHms(form.to),
      where,
    };
  } else {
    const allDay = form.allDay || form.efrom === form.eto;
    body = {
      campaign_id: form.show,
      kind: 'event',
      name: String(form.evName || '').trim(),
      start_date: form.start,
      end_date: form.end,
      start_time: allDay ? null : minToHms(form.efrom),
      end_time: allDay ? null : minToHms(form.eto),
      where,
    };
  }
  if (resolve) body.resolve = resolve;
  return body;
}

/** The old page could store a dated row as `weekly`; the new page lists every dated row with the events. */
export const isEventRule = (r) => r.kind === 'event' || (!!r.start_date && !!r.end_date);

/** `where` of an overview rule -> the UI shape. A legacy `mixed` rule (groups + screens) is shown as the screens it covers. */
export function whereFromApi(w, groups = []) {
  if (!w) return { mode: 'all', ids: [] };
  if (w.mode === 'all') return { mode: 'all', ids: [] };
  if (w.mode === 'groups' || w.mode === 'screens') return { mode: w.mode, ids: [...(w.ids || [])] };
  const ids = new Set();
  (w.ids || []).forEach((id) => {
    const g = groups.find((x) => x.id === id);
    if (g) g.screens.forEach((s) => ids.add(s.id)); else ids.add(id);
  });
  return { mode: 'screens', ids: [...ids] };
}

/**
 * One overview rule (`GET /campaign-schedules/overview` -> rules[]) -> the shape the page works with.
 * Both times NULL = all day: weekly -> from === to === 0, event -> allDay.
 */
export function ruleFromApi(r, groups = []) {
  const from = hmsToMin(r.start_time);
  const to = hmsToMin(r.end_time);
  const allDay = from === null || to === null;
  const base = {
    id: r.id,
    show: r.campaign?.id ?? null,
    showName: r.campaign?.name ?? null,
    where: whereFromApi(r.where, groups),
    screensCount: r.where?.screens_count ?? null,
    on: r.is_active !== false,
    priority: r.priority ?? 0,
    createdAt: r.created_at ?? null,
    excluded: r.excluded_dates || [],
    // history («السابقة»): when «إنهاء الآن» cut this rule's last run short (UTC instant), else null
    endedEarlyMs: r.ended_early_at ? (Date.parse(r.ended_early_at) || null) : null,
    // the engine reads the stored shape
    spec: {
      id: r.id,
      kind: r.kind || 'weekly',
      priority: r.priority ?? 0,
      start_time: r.start_time ?? null,
      end_time: r.end_time ?? null,
      days_of_week: r.days_of_week ?? null,
      start_date: r.start_date ?? null,
      end_date: r.end_date ?? null,
      excluded_dates: r.excluded_dates || [],
      is_active: r.is_active !== false,
      created_at: r.created_at ?? null,
    },
  };
  if (isEventRule(r)) {
    // a ONE-TIME show = a single-day event WITH hours; 00:00 as its end means "until the end of the day"
    const once = r.start_date === r.end_date && !allDay;
    return {
      ...base, type: 'event', once, name: r.name || r.campaign?.name || '', start: r.start_date, end: r.end_date, allDay,
      from: allDay ? 0 : from, to: allDay ? 0 : (once && to === 0 && from > 0 ? 1440 : to),
    };
  }
  return { ...base, type: 'rule', name: r.name || '', days: maskToDays(r.days_of_week), from: allDay ? 0 : from, to: allDay ? 0 : to };
}

/** The add/edit form (the prototype's `F`) for an existing rule or event. */
export function formFromRule(item) {
  const common = { step: 2, edit: { kind: item.type, id: item.id }, show: item.show, where: { mode: item.where.mode, ids: [...item.where.ids] }, q: '', resolve: 'new' };
  if (item.type === 'event' && item.once) {
    return { ...common, kind: 'once', onow: 'custom', odate: item.start, ofrom: item.from, oto: item.to, oname: item.name, occ: null, occF: 'all', days: [...EVERY_DAY], preset: 'morning', from: 360, to: 720, evName: '', start: item.start, end: item.end, allDay: true, efrom: 480, eto: 1380, tpl: null };
  }
  if (item.type === 'event') {
    return { ...common, kind: 'event', occ: 'custom', occF: 'all', onow: '60', odate: item.start, ofrom: 0, oto: 60, oname: '', evName: item.name, start: item.start, end: item.end, allDay: item.allDay, efrom: item.allDay ? 480 : item.from, eto: item.allDay ? 1380 : item.to, days: [...EVERY_DAY], preset: 'morning', from: 360, to: 720, tpl: null };
  }
  const preset = PRESETS.find((p) => p.from === item.from && p.to === item.to);
  return { ...common, kind: 'rule', occ: null, occF: 'all', onow: '60', odate: '', ofrom: 0, oto: 60, oname: '', days: [...item.days], from: item.from, to: item.to, preset: preset ? preset.id : 'custom', evName: '', start: '', end: '', allDay: true, efrom: 480, eto: 1380, tpl: null };
}

/** Weekly presets of step 2 (minutes). `custom` has no fixed hours. */
export const PRESETS = [
  { id: 'morning', from: 360, to: 720 },
  { id: 'noon', from: 720, to: 960 },
  { id: 'evening', from: 960, to: 0 },
  { id: 'allday', from: 0, to: 0 },
  { id: 'custom' },
];

/**
 * «إعادة للعام القادم»: the same event one year later (both dates +1 year; name, campaign, hours and screens kept), as a NEW
 * form that opens on the summary step so it is reviewed before it is saved.
 */
export function nextYearForm(item) {
  return {
    ...formFromRule({ ...item, start: addYears(item.start, 1), end: addYears(item.end, 1) }),
    step: 4,
    edit: null,
  };
}

/** Events show the repeat action once they ended, or end within this many days. */
export const REPEAT_WINDOW_DAYS = 60;
export function canRepeatNextYear(ev, today) {
  return dayDiff(today, ev.end) <= REPEAT_WINDOW_DAYS;
}

/* ───────────────────────── 3. engine (occurrence-based) ───────────────────────── */

const SPECIFICITY = { device: 0, group: 1, company: 2 };
export const specificityOf = (type) => (type in SPECIFICITY ? SPECIFICITY[type] : 99);

const dtfCache = new Map();
function dtf(tz) {
  let f = dtfCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' });
    dtfCache.set(tz, f);
  }
  return f;
}

/** Local wall-clock fields of an instant in `tz`. */
export function localParts(ms, tz) {
  const p = dtf(tz).formatToParts(new Date(ms));
  const g = (t) => parseInt(p.find((x) => x.type === t).value, 10);
  const y = g('year'); const mo = g('month'); const d = g('day');
  const h = g('hour') % 24;
  const date = `${y}-${pad2(mo)}-${pad2(d)}`;
  return { date, dow: dowOf(date), min: h * 60 + g('minute'), sec: g('second'), y, mo, d, h };
}

/** Offset (ms) of `tz` at the instant `ms`: local wall clock read as UTC, minus the instant. */
function offsetAt(ms, tz) {
  const p = localParts(ms, tz);
  return Date.UTC(p.y, p.mo - 1, p.d, p.h, parseInt(String(p.min % 60), 10), p.sec) - Math.floor(ms / 1000) * 1000;
}

/**
 * Wall clock -> instant (ms). A nonexistent local time (spring-forward gap) resolves with the offset BEFORE the transition
 * (it shifts forward); an ambiguous one (fall-back overlap) resolves to the FIRST occurrence. Same rule as PHP and the player.
 */
export function zonedToUtc(ymd, hms, tz) {
  const [h, mi, s] = String(hms).split(':').map((x) => parseInt(x, 10) || 0);
  const { y, m, d } = ymdParts(ymd);
  const guess = Date.UTC(y, m - 1, d, h, mi, s);
  const o1 = offsetAt(guess - 86400000, tz);
  const o2 = offsetAt(guess + 86400000, tz);
  const t1 = guess - o1;
  const t2 = guess - o2;
  const ok1 = offsetAt(t1, tz) === o1;
  const ok2 = offsetAt(t2, tz) === o2;
  if (ok1 && ok2) return Math.min(t1, t2); // ambiguous: the first occurrence
  if (ok1) return t1;
  if (ok2) return t2;
  return t1; // gap: the offset before the transition
}

/** The [start, end) window (ms) of the occurrence of `rule` that STARTS on local date `ymd`, or null. */
export function occurrenceWindow(rule, ymd, tz) {
  if (rule.is_active === false) return null;
  const kind = rule.kind || 'weekly';
  const sd = rule.start_date ? String(rule.start_date).slice(0, 10) : null;
  const ed = rule.end_date ? String(rule.end_date).slice(0, 10) : null;
  if (kind === 'event' && (sd === null || ed === null)) return null;
  if (sd !== null && ymd < sd) return null;
  if (ed !== null && ymd > ed) return null;
  const mask = rule.days_of_week ?? null;
  if (mask !== null && !(mask & (1 << uiDayToBit(dowOf(ymd))))) return null;
  if (Array.isArray(rule.excluded_dates) && rule.excluded_dates.includes(ymd)) return null;

  const st = rule.start_time ?? null;
  const et = rule.end_time ?? null;
  if (st === null && et === null) return [zonedToUtc(ymd, '00:00:00', tz), zonedToUtc(addDays(ymd, 1), '00:00:00', tz)];
  if (st === null || et === null) return null;
  const s = String(st).slice(0, 8);
  const e = String(et).slice(0, 8);
  return [zonedToUtc(ymd, s, tz), zonedToUtc(e <= s ? addDays(ymd, 1) : ymd, e, tz)];
}

const createdKey = (v) => (v ? Date.parse(v) || 0 : 0);

/** Winner order (decision 4): event over weekly, higher priority, device < group < company, newest, smaller id. */
export function compareRules(a, b) {
  const ka = a.kind === 'event' ? 0 : 1;
  const kb = b.kind === 'event' ? 0 : 1;
  if (ka !== kb) return ka - kb;
  if ((a.priority || 0) !== (b.priority || 0)) return (b.priority || 0) - (a.priority || 0);
  const sa = a.specificity ?? 99;
  const sb = b.specificity ?? 99;
  if (sa !== sb) return sa - sb;
  const ca = createdKey(a.created_at);
  const cb = createdKey(b.created_at);
  if (ca !== cb) return cb - ca;
  return String(a.id) < String(b.id) ? -1 : (String(a.id) > String(b.id) ? 1 : 0);
}

/** Every occurrence window of `rules` that starts on a local day in [fromYmd, toYmd]. */
export function buildIntervals(rules, tz, fromYmd, toYmd) {
  const out = [];
  const n = dayDiff(fromYmd, toYmd);
  rules.forEach((rule) => {
    for (let i = 0; i <= n; i += 1) {
      const w = occurrenceWindow(rule, addDays(fromYmd, i), tz);
      if (w) out.push({ s: w[0], e: w[1], rule });
    }
  });
  return out;
}

/** The winning rule at instant `ms` among prepared intervals, or null. */
export function pickFromIntervals(intervals, ms) {
  let best = null;
  for (let i = 0; i < intervals.length; i += 1) {
    const iv = intervals[i];
    if (iv.s <= ms && ms < iv.e && (best === null || compareRules(iv.rule, best) < 0)) best = iv.rule;
  }
  return best;
}

/** The winner at an instant: the vectors-file contract (`now_utc`, `tz`, rules with `specificity`). */
export function pickV2(rules, nowMs, tz) {
  const today = localParts(nowMs, tz).date;
  return pickFromIntervals(buildIntervals(rules, tz, addDays(today, -1), today), nowMs);
}

/* ───────────────────────── 4. preview (what the page shows) ───────────────────────── */

/** Which of the page's rules/events cover which screen, and how specifically (device 0, group 1, company 2). */
export function specificityFor(where, screenId, groups) {
  if (where.mode === 'all') return 2;
  if (where.mode === 'screens') return where.ids.includes(screenId) ? 0 : null;
  const hit = groups.some((g) => where.ids.includes(g.id) && g.screens.some((s) => s.id === screenId));
  return hit ? 1 : null;
}

/** The engine rules that apply to one screen (paused ones included: the engine skips them). */
export function rulesForScreen(items, screenId, groups) {
  const out = [];
  items.forEach((it) => {
    const spec = specificityFor(it.where, screenId, groups);
    if (spec !== null) out.push({ ...it.spec, show: it.show, specificity: spec });
  });
  return out;
}

const stateOf = (rule, def) => (rule ? { show: rule.show, kind: rule.manual ? 'manual' : (rule.kind === 'event' ? 'event' : 'rule'), ...(rule.once ? { once: true } : {}) } : { show: def, kind: 'default' });

/** What one screen plays at an instant: { show, kind: 'event' | 'rule' | 'default' }. */
export function atScreen(rules, def, ms, tz) {
  return stateOf(pickV2(rules, ms, tz), def);
}

/**
 * Now / next for one screen, from the real window boundaries (no clock stepping).
 *   cur      what it plays now
 *   next     the first later moment the SHOW changes: { show, kind, atMs } or null within 8 days
 *   startedMs when the current show started (looking back 2 days), or null
 */
export function nowNext(rules, def, nowMs, tz, hold = null) {
  const today = localParts(nowMs, tz).date;
  const iv = buildIntervals(rules, tz, addDays(today, -3), addDays(today, 8));
  // a manual hold: the screen shows `hold.show` (what a person chose) until `hold.untilMs`, whatever the schedule says
  if (hold) iv.push({ s: nowMs - 1, e: hold.untilMs, rule: { id: '__hold', kind: 'event', priority: 1e9, specificity: -1, show: hold.show, manual: true } });
  const at = (ms) => stateOf(pickFromIntervals(iv, ms), def);
  const cur = at(nowMs);

  const set = new Set();
  iv.forEach((x) => { set.add(x.s); set.add(x.e); });
  const bounds = [...set].sort((a, b) => a - b);

  let next = null;
  for (let i = 0; i < bounds.length; i += 1) {
    const b = bounds[i];
    if (b > nowMs && b <= nowMs + 8 * 86400000) {
      const s = at(b);
      if (s.show !== cur.show) { next = { ...s, atMs: b }; break; }
    }
  }

  let startedMs = null;
  for (let i = bounds.length - 1; i >= 0; i -= 1) {
    const b = bounds[i];
    if (b <= nowMs && b >= nowMs - 2 * 86400000) {
      if (at(b - 1).show !== cur.show) { startedMs = b; break; }
    }
  }
  return { cur, next, startedMs };
}

/**
 * The week view for one screen: for each of the 7 days (Sunday first) the coloured segments, in 15-minute steps like the
 * prototype. `default` (the standing show) is not drawn: an empty lane means "your manual choice".
 */
export function weekSegments(rules, def, weekStartYmd, tz) {
  const iv = buildIntervals(rules, tz, addDays(weekStartYmd, -1), addDays(weekStartYmd, 6));
  const days = [];
  for (let d = 0; d < 7; d += 1) {
    const date = addDays(weekStartYmd, d);
    const segs = [];
    let cur = null;
    let a = 0;
    // 15-minute steps like the prototype, plus the real boundary minutes of the day, so a 8:34-8:38 window is drawn as itself
    const marks = new Set([1440]);
    for (let m = 0; m < 1440; m += 15) marks.add(m);
    iv.forEach((x) => [x.s, x.e].forEach((ms) => { const p = localParts(ms, tz); if (p.date === date) marks.add(p.min); }));
    [...marks].sort((x, y) => x - y).forEach((m) => {
      let key = null;
      if (m < 1440) {
        const st = stateOf(pickFromIntervals(iv, zonedToUtc(date, minToHms(m), tz)), def);
        key = `${st.show}|${st.kind}`;
      }
      if (cur === null) { cur = key; a = m; return; }
      if (key !== cur) { segs.push({ a, b: m, key: cur }); cur = key; a = m; }
    });
    days.push({ date, dow: dowOf(date), segs: segs.map((s) => { const [show, kind] = s.key.split('|'); return { a: s.a, b: s.b, show, kind }; }) });
  }
  return days;
}

/** The part of the day (in minutes) a weekly/event window covers: [[a, b]], wrapping windows split in two. */
export function daySegments(from, to) {
  if (from === to) return [[0, 1440]];
  if (from < to) return [[from, to]];
  const out = [[from, 1440]];
  if (to > 0) out.push([0, to]);
  return out;
}

/** First overlap of two same-day windows, or null (the prototype's `overlap`). */
export function overlap(a1, b1, a2, b2) {
  let best = null;
  daySegments(a1, b1).forEach((x) => daySegments(a2, b2).forEach((y) => {
    const s = Math.max(x[0], y[0]);
    const e = Math.min(x[1], y[1]);
    if (e > s && !best) best = [s, e];
  }));
  return best;
}

/** Show colour slot: a stable 1..7 per campaign id (the prototype's --s1 ... --s7 tokens). */
export function colorSlot(id) {
  let h = 0;
  const s = String(id);
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return (h % 7) + 1;
}

/** Wall-clock "now" in a time zone, in the page's shape. */
export function nowIn(tz, ms = Date.now()) {
  const p = localParts(ms, tz);
  return { date: p.date, dow: p.dow, min: p.min, ms };
}

/* ───────────────────────── screens, targets, tally ───────────────────────── */

/** Every screen id of the account, in group order. `groups` = [{ id, name, screens: [{ id, ... }] }]. */
export const allScreenIds = (groups) => groups.flatMap((g) => g.screens.map((s) => s.id));

/** The screens a rule's `where` covers. */
export function screensOfWhere(w, groups) {
  if (w.mode === 'all') return allScreenIds(groups);
  if (w.mode === 'groups') return groups.filter((g) => g.id !== null && w.ids.includes(g.id)).flatMap((g) => g.screens.map((s) => s.id));
  return [...w.ids];
}

/** The screens of a page target: 'all', a group id, or one screen id. */
export function targetScreens(target, groups) {
  if (target === 'all') return allScreenIds(groups);
  const g = groups.find((x) => x.id === target);
  return g ? g.screens.map((s) => s.id) : [target];
}

/** Does `where` touch any screen of the target? */
export function hitsTarget(where, target, groups) {
  const t = new Set(targetScreens(target, groups));
  return screensOfWhere(where, groups).some((id) => t.has(id));
}

/**
 * What every screen of a target plays right now, evaluated per screen in the screen's own time zone, but with the window
 * math done once per (rule, zone) instead of once per screen. Returns { [screenId]: { show, kind } }.
 */
export function tallyNow(items, screenIds, groups, def, nowMs, fallbackTz, tzOf = () => null, held = {}) {
  const groupOf = new Map();
  groups.forEach((g) => g.screens.forEach((s) => groupOf.set(s.id, g.id)));
  const wins = new Map(); // tz -> [{ s, e, item }]
  const windowsFor = (tz) => {
    let w = wins.get(tz);
    if (!w) {
      const today = localParts(nowMs, tz).date;
      w = [];
      items.forEach((item) => {
        [addDays(today, -1), today].forEach((day) => {
          const x = occurrenceWindow(item.spec, day, tz);
          if (x && x[0] <= nowMs && nowMs < x[1]) w.push(item);
        });
      });
      wins.set(tz, w);
    }
    return w;
  };
  const out = {};
  screenIds.forEach((id) => {
    if (held[id]) { out[id] = { show: held[id].show ?? def, kind: 'manual', hold: held[id] }; return; }
    const tz = tzOf(id) || fallbackTz;
    let best = null;
    windowsFor(tz).forEach((item) => {
      let sp = null;
      if (item.where.mode === 'all') sp = 2;
      else if (item.where.mode === 'screens') sp = item.where.ids.includes(id) ? 0 : null;
      else sp = item.where.ids.includes(groupOf.get(id)) ? 1 : null;
      if (sp === null) return;
      const cand = { ...item.spec, show: item.show, specificity: sp, once: item.once };
      if (best === null || compareRules(cand, best) < 0) best = cand;
    });
    out[id] = { ...stateOf(best, def), ...(best ? { ruleId: best.id } : {}) };
  });
  return out;
}

/**
 * A `schedule_paused` block ({ schedule_name, until_local, until_at, reason }) -> the page's hold, or null when the block has no
 * usable end (a hold without an end instant cannot be drawn, and must never be dereferenced blind).
 */
export function holdFromBlock(b) {
  if (!b) return null;
  const until = Date.parse(b.until_at);
  if (!Number.isFinite(until)) return null;
  return { until, untilAt: b.until_at, untilLocal: b.until_local, scheduleName: b.schedule_name, reason: b.reason };
}

/** A status-many screen ({ now, next }) in the page's shape; `itemsById` tells event from weekly for the winner. */
export function serverInfo(s, itemsById) {
  const src = s.now?.source;
  const winner = s.now?.rule_id ? itemsById.get(s.now.rule_id) : null;
  const cur = { show: s.now?.campaign_id ?? null, kind: src === 'schedule' ? (winner?.type === 'event' ? 'event' : 'rule') : 'default' };
  if (s.schedule_paused) {
    cur.kind = 'manual';
    cur.hold = holdFromBlock(s.schedule_paused);
  }
  if (winner && cur.kind !== 'manual') { cur.ruleId = winner.id; if (winner.once) cur.once = true; }
  const atMs = s.next ? Date.parse(s.next.at) : NaN;
  const next = s.next && Number.isFinite(atMs) ? { show: s.next.campaign_id ?? null, kind: s.next.source === 'schedule' ? 'rule' : 'default', atMs } : null;
  return { cur, next };
}

/**
 * The order screens are ranked by when nothing else decides (a tie in the headline, the screen the «next» line speaks about): the
 * company's group order, then by name, then id inside a group. It does NOT use the order the API lists the screens in: the
 * backend sorts a group's screens by `COALESCE(last_login, created_at)`, which changes every time a screen logs in, so that order
 * shuffled the headline between two refreshes.
 */
export function stableRanks(groups) {
  const byName = (a, b) => {
    const an = a.name || '';
    const bn = b.name || '';
    if (an !== bn) return an < bn ? -1 : 1;
    return String(a.id) < String(b.id) ? -1 : (String(a.id) > String(b.id) ? 1 : 0);
  };
  const out = new Map();
  groups.forEach((g) => [...g.screens].sort(byName).forEach((s) => out.set(s.id, out.size)));
  return out;
}

/**
 * Groups screens that play the same thing into the now-card tally and orders it so the headline is deterministic:
 * a group inside an active scheduled window (rule / event) wins, otherwise the largest group; ties by the stable screen order.
 * `cur(id)` -> { show, kind, once?, ruleId?, hold? }. Screens held until a different minute are different groups.
 */
export function buildTally(ids, cur, rank) {
  const by = new Map();
  ids.forEach((id) => {
    const c = cur(id);
    const holdMin = c.kind === 'manual' && c.hold ? Math.floor(c.hold.until / 60000) : '';
    const key = `${c.show}|${c.kind}|${holdMin}`;
    if (!by.has(key)) by.set(key, { show: c.show, kind: c.kind, once: c.once, ids: [], ruleIds: new Set(), hold: c.hold });
    const g = by.get(key);
    g.ids.push(id);
    if (c.ruleId) g.ruleIds.add(c.ruleId);
  });
  const r = (id) => rank.get(id) ?? Number.MAX_SAFE_INTEGER;
  const groups = [...by.values()];
  groups.forEach((g) => { g.rep = g.ids.reduce((best, id) => (r(id) < r(best) ? id : best), g.ids[0]); g.rank = r(g.rep); });
  const scheduled = (g) => (g.kind === 'rule' || g.kind === 'event' ? 0 : 1);
  return groups.sort((a, b) => scheduled(a) - scheduled(b) || b.ids.length - a.ids.length || a.rank - b.rank);
}

/* ───────────────────────── one-time shows ("مرة واحدة") ───────────────────────── */

/** The state of a fresh one-time form: now / 30 min / 1 h / until the end of the day, or a day and time of the person's choice. */
export function onceTimes(preset, nowMin) {
  if (preset === 'custom') return { ofrom: 1080, oto: 1140 };
  return { ofrom: nowMin, oto: preset === '30' ? nowMin + 30 : (preset === '60' ? nowMin + 60 : 1440) };
}

/** Is the one-time form complete and not in the past? (v4 `onceValid`) */
export function onceValid(F, now) {
  if (!F.odate || F.odate < now.date) return false;
  if (F.oto !== 1440 && F.oto <= F.ofrom) return false;
  if (F.odate === now.date && F.oto !== 1440 && F.oto <= now.min) return false;
  return true;
}

/** Why it is not valid: 'past' | 'order' | null. */
export function onceProblem(F, now) {
  if (F.odate < now.date || (F.odate === now.date && F.oto !== 1440 && F.oto <= now.min)) return 'past';
  if (F.oto !== 1440 && F.oto <= F.ofrom) return 'order';
  return null;
}

/** «السابقة»: how far back the history goes, in days. */
export const HISTORY_DAYS = 90;

/** An event / one-time show that is over (its last day passed, or a one-time show whose hours ended today). */
export const isPastEvent = (e, now) => e.type === 'event' && (e.end < now.date || onceFinished(e, now));

/** The history list: events that ended within the last 90 days, the most recent first. */
export function pastEvents(events, now) {
  const limit = addDays(now.date, -HISTORY_DAYS);
  return events
    .filter((e) => isPastEvent(e, now) && e.end >= limit)
    .sort((a, b) => (a.end !== b.end ? (a.end < b.end ? 1 : -1) : ((b.to || 0) - (a.to || 0)) || (String(a.id) < String(b.id) ? -1 : 1)));
}

/**
 * «استخدام مرة أخرى»: a new form from an ended show or event, opened on the «when» step. A one-time show comes back for today
 * with the same hours; a multi-day event starts in 7 days and lasts as long as before. Campaign, name and screens are kept.
 */
export function reuseForm(item, today) {
  if (item.once) return { ...formFromRule(item), edit: null, odate: today, step: 2 };
  const start = addDays(today, 7);
  return { ...formFromRule({ ...item, start, end: addDays(start, dayDiff(item.start, item.end)) }), edit: null, step: 2 };
}

/** A one-time show that already ended today is not listed any more. */
export function onceFinished(e, now) {
  return !!e.once && e.end === now.date && e.to !== e.from && e.to !== 1440 && e.to <= now.min;
}

/** Is minute `m` inside the daily window [from, to) (equal = all day, to <= from wraps midnight)? */
export function inRange(from, to, m) {
  if (from === to) return true;
  if (from < to) return m >= from && m < to;
  return m >= from || m < to;
}
